import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KeyRound, Pencil, Plus, Search, Trash2, User } from 'lucide-react';
import type { EmployeeDto, EmployeeListResponse } from '@gc/shared';

import { api } from '@/lib/api';
import { EmployeeDrawer } from './EmployeeDrawer';
import { ResetPasswordDialog } from './ResetPasswordDialog';
import { ghostPillClass, inputClass, pillClass } from './controls';
import { toFieldErrors, useResource } from './useSettings';

/**
 * Employees (SPEC §6.4).
 *
 * Columns are fixed by the spec and must not be trimmed:
 * Image · Name · Phone Number · Username · Reset Password · Edit · Delete.
 */
export function EmployeesPage() {
  const { t } = useTranslation();

  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [editing, setEditing] = useState<EmployeeDto | null>(null);
  const [creating, setCreating] = useState(false);
  const [resetting, setResetting] = useState<EmployeeDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Typing a name should not fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const query = debounced ? `?search=${encodeURIComponent(debounced)}` : '';
  const { data, loading, reload } = useResource<EmployeeListResponse>(`/employees${query}`);

  async function handleDelete(employee: EmployeeDto): Promise<void> {
    if (!window.confirm(t('settings.employees.confirmDelete', { name: employee.name }))) return;
    setError(null);
    try {
      await api.delete(`/employees/${employee.id}`);
      await reload();
    } catch (caught) {
      setError(toFieldErrors(caught, t('settings.employees.deleteFailed'))[0]?.message ?? null);
    }
  }

  const employees = data?.employees ?? [];

  return (
    <div className="p-6">
      <header className="mb-4 flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold text-content">{t('settings.nav.employees')}</h1>

        <div className="relative">
          <Search
            className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-content-muted"
            style={{ insetInlineStart: '0.6rem' }}
            aria-hidden
          />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('settings.employees.searchPlaceholder')}
            className={`${inputClass} w-64 ps-8`}
          />
        </div>

        <div className="flex-1" />

        <button type="button" onClick={() => setCreating(true)} className={pillClass}>
          <span className="inline-flex items-center gap-1">
            <Plus className="h-4 w-4" aria-hidden />
            {t('settings.employees.addNew')}
          </span>
        </button>
      </header>

      {error ? (
        <p role="alert" className="mb-3 text-sm text-danger">
          {error}
        </p>
      ) : null}

      {/* Phone: a card per employee — same data, same actions, no sideways drag. */}
      <div className="space-y-2 md:hidden">
        {loading && employees.length === 0 ? (
          <p className="text-sm text-content-muted">{t('common.loading')}</p>
        ) : employees.length === 0 ? (
          <p className="text-sm text-content-muted">{t('common.noResults')}</p>
        ) : (
          employees.map((employee) => (
            <div
              key={employee.id}
              className="flex items-center gap-3 rounded-card border border-subtle bg-raised p-3"
            >
              <span className="grid h-10 w-10 shrink-0 place-items-center overflow-hidden rounded-full border border-subtle bg-input">
                {employee.avatarUrl ? (
                  <img src={employee.avatarUrl} alt="" className="h-full w-full object-cover" />
                ) : (
                  <User className="h-4 w-4 text-content-muted" aria-hidden />
                )}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-content">
                  {employee.name}
                  {employee.isOwner ? (
                    <span className="ms-2 rounded-full bg-primary/20 px-2 py-0.5 text-xs font-normal text-primary">
                      {t('settings.employees.owner')}
                    </span>
                  ) : null}
                  {!employee.isActive ? (
                    <span className="ms-2 text-xs font-normal text-content-muted">
                      ({t('settings.employees.inactive')})
                    </span>
                  ) : null}
                </p>
                <p className="truncate text-xs text-content-muted" dir="ltr">
                  {employee.username}
                  {employee.phone ? ` · ${employee.phone}` : ''}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <IconButton
                  label={t('settings.employees.resetPassword')}
                  onClick={() => setResetting(employee)}
                >
                  <KeyRound className="h-4 w-4" aria-hidden />
                </IconButton>
                <IconButton label={t('common.edit')} onClick={() => setEditing(employee)}>
                  <Pencil className="h-4 w-4" aria-hidden />
                </IconButton>
                <IconButton
                  label={t('common.delete')}
                  danger
                  disabled={employee.isOwner}
                  title={employee.isOwner ? t('settings.employees.cannotDeleteOwner') : undefined}
                  onClick={() => void handleDelete(employee)}
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </IconButton>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="overflow-x-auto rounded-card border border-subtle max-md:hidden">
        <table className="w-full min-w-[52rem] border-collapse text-sm">
          <thead>
            <tr className="bg-raised text-content-secondary">
              <Th>{t('common.image')}</Th>
              <Th>{t('common.name')}</Th>
              <Th>{t('common.phoneNumber')}</Th>
              <Th>{t('common.username')}</Th>
              <Th>{t('settings.employees.resetPassword')}</Th>
              <Th>{t('common.edit')}</Th>
              <Th>{t('common.delete')}</Th>
            </tr>
          </thead>
          <tbody>
            {loading && employees.length === 0 ? (
              <tr>
                <td colSpan={7} className="p-4 text-center text-content-muted">
                  {t('common.loading')}
                </td>
              </tr>
            ) : employees.length === 0 ? (
              <tr>
                <td colSpan={7} className="p-4 text-center text-content-muted">
                  {t('common.noResults')}
                </td>
              </tr>
            ) : (
              employees.map((employee, index) => (
                <tr
                  key={employee.id}
                  className={index % 2 === 0 ? 'bg-[var(--row-even)]' : 'bg-[var(--row-odd)]'}
                >
                  <Td>
                    <span className="grid h-9 w-9 place-items-center overflow-hidden rounded-full border border-subtle bg-input">
                      {employee.avatarUrl ? (
                        <img
                          src={employee.avatarUrl}
                          alt=""
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <User className="h-4 w-4 text-content-muted" aria-hidden />
                      )}
                    </span>
                  </Td>
                  <Td>
                    {/* No dir override: an Arabic name must render RTL inside an
                        LTR table, which the browser does correctly on its own. */}
                    <span className="text-content">{employee.name}</span>
                    {employee.isOwner ? (
                      <span className="ms-2 rounded-full bg-primary/20 px-2 py-0.5 text-xs text-primary">
                        {t('settings.employees.owner')}
                      </span>
                    ) : null}
                    {!employee.isActive ? (
                      <span className="ms-2 text-xs text-content-muted">
                        ({t('settings.employees.inactive')})
                      </span>
                    ) : null}
                  </Td>
                  <Td>
                    <span className="tabular text-content-secondary" dir="ltr">
                      {employee.phone ?? '—'}
                    </span>
                  </Td>
                  <Td>
                    <span className="text-content-secondary" dir="ltr">
                      {employee.username}
                    </span>
                  </Td>
                  <Td>
                    <IconButton
                      label={t('settings.employees.resetPassword')}
                      onClick={() => setResetting(employee)}
                    >
                      <KeyRound className="h-4 w-4" aria-hidden />
                    </IconButton>
                  </Td>
                  <Td>
                    <IconButton label={t('common.edit')} onClick={() => setEditing(employee)}>
                      <Pencil className="h-4 w-4" aria-hidden />
                    </IconButton>
                  </Td>
                  <Td>
                    <IconButton
                      label={t('common.delete')}
                      danger
                      // The owner account cannot be removed (SPEC §3); disabled
                      // with a reason rather than failing on click.
                      disabled={employee.isOwner}
                      title={
                        employee.isOwner ? t('settings.employees.cannotDeleteOwner') : undefined
                      }
                      onClick={() => void handleDelete(employee)}
                    >
                      <Trash2 className="h-4 w-4" aria-hidden />
                    </IconButton>
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {creating ? (
        <EmployeeDrawer
          employee={null}
          onClose={() => setCreating(false)}
          onSaved={() => void reload()}
        />
      ) : null}

      {editing ? (
        <EmployeeDrawer
          employee={editing}
          onClose={() => setEditing(null)}
          onSaved={() => void reload()}
        />
      ) : null}

      {resetting ? (
        <ResetPasswordDialog employee={resetting} onClose={() => setResetting(null)} />
      ) : null}
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="border-b border-subtle p-3 text-start font-medium">{children}</th>;
}

function Td({ children }: { children: React.ReactNode }) {
  return <td className="border-b border-subtle p-3 align-middle">{children}</td>;
}

function IconButton({
  children,
  label,
  onClick,
  danger,
  disabled,
  title,
}: {
  children: React.ReactNode;
  label: string;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
  title?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title ?? label}
      aria-label={label}
      className={[
        'grid h-8 w-8 place-items-center rounded-full text-content-secondary',
        disabled ? 'cursor-not-allowed opacity-40' : 'hover:bg-hover',
        danger && !disabled ? 'hover:text-danger' : '',
      ].join(' ')}
    >
      {children}
    </button>
  );
}

export { ghostPillClass };
